import React, { useId } from 'react';
import { PALETTE, FONT } from './tokens';
import type { ShellTheme } from './tokens';
import { RubyGem } from './RubyGem';
import { ShellButton } from './ShellButton';
import { PERMISSION_COPY } from '../permissions';
import type { ShellPermission } from '../permissions';

const fullScreen = (theme: ShellTheme): React.CSSProperties => ({
  position: 'fixed', inset: 0, zIndex: 2147483000, background: PALETTE[theme].ground, color: PALETTE[theme].ink,
  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 20,
  padding: 'max(24px, env(safe-area-inset-top)) 24px max(24px, env(safe-area-inset-bottom))', font: `16px ${FONT}`, textAlign: 'center',
});
const title: React.CSSProperties = { margin: 0, fontSize: 22, fontWeight: 700 };
const actions: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 10, width: '100%', maxWidth: 360 };

/** Blocks the app until the store update is installed (gate = required). Always dark, like the splash. */
export function UpdateRequiredScreen({ platform, onOpenStore }: { platform: 'ios' | 'android'; onOpenStore: () => void }) {
  return (
    <div style={fullScreen('dark')}>
      <RubyGem size={72} />
      <h1 style={title}>Update required</h1>
      <div style={actions}>
        <ShellButton theme="dark" primary onClick={onOpenStore}>{platform === 'ios' ? 'Open App Store' : 'Open Play Store'}</ShellButton>
      </div>
    </div>
  );
}

function Sheet({ theme, labelledBy, children }: { theme: ShellTheme; labelledBy: string; children: React.ReactNode }) {
  const p = PALETTE[theme];
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 2147482000, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div
        role="dialog" aria-modal="true" aria-labelledby={labelledBy}
        style={{
          width: '100%', maxWidth: 480, background: p.surface, color: p.ink, borderRadius: '20px 20px 0 0',
          padding: '24px 20px max(20px, env(safe-area-inset-bottom))', display: 'flex', flexDirection: 'column', gap: 14,
          alignItems: 'center', textAlign: 'center', font: `16px ${FONT}`, borderTop: `1px solid ${p.line}`,
        }}
      >
        {children}
      </div>
    </div>
  );
}

/** Store update available but not required (gate = recommended). */
export function UpdateAvailableSheet({ theme, onUpdate, onLater }: { theme: ShellTheme; onUpdate: () => void; onLater: () => void }) {
  const id = useId();
  return (
    <Sheet theme={theme} labelledBy={id}>
      <RubyGem size={40} />
      <h2 id={id} style={{ ...title, fontSize: 18 }}>New version ready</h2>
      <div style={{ ...actions, flexDirection: 'row' }}>
        <ShellButton theme={theme} onClick={onLater}>Later</ShellButton>
        <ShellButton theme={theme} primary onClick={onUpdate}>Update</ShellButton>
      </div>
    </Sheet>
  );
}

/** Our explanation BEFORE the one-time system prompt. "Not now" keeps the system prompt unused. */
export function PermissionPrePrompt({ permission, platform, theme, onAllow, onNotNow }: {
  permission: ShellPermission; platform: 'ios' | 'android'; theme: ShellTheme; onAllow: () => void; onNotNow: () => void;
}) {
  const id = useId();
  const copy = PERMISSION_COPY[permission];
  return (
    <Sheet theme={theme} labelledBy={id}>
      <h2 id={id} style={{ ...title, fontSize: 18 }}>{copy.title(platform)}</h2>
      <p style={{ margin: 0, color: PALETTE[theme].muted }}>{copy.body}</p>
      <div style={actions}>
        <ShellButton theme={theme} primary onClick={onAllow}>Allow</ShellButton>
        <ShellButton theme={theme} onClick={onNotNow}>Not now</ShellButton>
      </div>
    </Sheet>
  );
}

/** Inline notice where a denied permission is needed. */
export function TurnOnInSettings({ permission, theme, onOpenSettings }: { permission: ShellPermission; theme: ShellTheme; onOpenSettings: () => void }) {
  const p = PALETTE[theme];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 16, borderRadius: 14, border: `1px solid ${p.line}`, background: p.surface, color: p.ink, font: `15px ${FONT}` }}>
      <span>{PERMISSION_COPY[permission].offLine}</span>
      <ShellButton theme={theme} primary onClick={onOpenSettings}>Turn on in Settings</ShellButton>
    </div>
  );
}

/** A page switched off in the app menu (spec §5.3). */
export function OpenOnWebScreen({ theme, onOpenWeb, onBack }: { theme: ShellTheme; onOpenWeb: () => void; onBack: () => void }) {
  return (
    <div style={{ ...fullScreen(theme), position: 'static', minHeight: '100%', inset: undefined }}>
      <RubyGem size={56} />
      <h1 style={title}>Available on the web</h1>
      <p style={{ margin: 0, color: PALETTE[theme].muted }}>This page isn't in the app.</p>
      <div style={actions}>
        <ShellButton theme={theme} primary onClick={onOpenWeb}>Open on the web</ShellButton>
        <ShellButton theme={theme} onClick={onBack}>Back</ShellButton>
      </div>
    </div>
  );
}
